import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./tableScrollSync.scss";

/**
 * Adds a horizontal scrollbar *above* a wide table, kept in sync with the
 * table's own one.
 *
 * A 16-column table only exposes its scrollbar at the very bottom of the grid,
 * so reaching the right-hand columns means scrolling the page down, dragging
 * sideways, then scrolling back up. This mirrors that scrollbar at the top of the
 * table where the columns start, and drives it both ways.
 *
 * It hides itself when there is nothing to scroll, so a narrow table is
 * unaffected.
 *
 * `syncKey` re-attaches the listeners when the table re-renders its scroll
 * container (antd swaps it when the row set goes from empty to populated).
 */
const TableScrollSync = ({ children, syncKey }) => {
  const wrapRef = useRef(null);
  const topRef = useRef(null);
  const scrollerRef = useRef(null);
  // Guards the two scroll handlers against echoing each other.
  const syncing = useRef(false);

  const [contentWidth, setContentWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const inner = scroller.querySelector("table");
    setContentWidth(inner?.scrollWidth || scroller.scrollWidth || 0);
    setViewportWidth(scroller.clientWidth || 0);
  }, []);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const top = topRef.current;
    if (!wrap || !top) return undefined;

    // antd puts the horizontal overflow on .ant-table-content when there is no
    // vertical scroll, and on .ant-table-body when there is.
    const scroller = wrap.querySelector(".ant-table-content, .ant-table-body");
    scrollerRef.current = scroller;
    if (!scroller) return undefined;

    measure();

    const onTopScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      scroller.scrollLeft = top.scrollLeft;
      syncing.current = false;
    };

    const onTableScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      top.scrollLeft = scroller.scrollLeft;
      syncing.current = false;
    };

    top.addEventListener("scroll", onTopScroll, { passive: true });
    scroller.addEventListener("scroll", onTableScroll, { passive: true });

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(scroller);
      const inner = scroller.querySelector("table");
      if (inner) observer.observe(inner);
    }
    window.addEventListener("resize", measure);

    return () => {
      top.removeEventListener("scroll", onTopScroll);
      scroller.removeEventListener("scroll", onTableScroll);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [measure, syncKey]);

  // Column widths settle a frame after the rows paint.
  useEffect(() => {
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [measure, syncKey]);

  const scrollable = contentWidth > viewportWidth + 1;

  return (
    <div className="tableScrollSync" ref={wrapRef}>
      <div
        className="tableScrollSync__top"
        ref={topRef}
        aria-hidden="true"
        style={scrollable ? undefined : { display: "none" }}
      >
        <div style={{ width: contentWidth, height: 1 }} />
      </div>
      {children}
    </div>
  );
};

export default TableScrollSync;
